import React, { useState, useEffect, useCallback } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Chip,
  Typography,
  Box,
} from '@mui/material';
import { ClipboardText } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useThemeStyles } from '../../../theme/useThemeStyles';
import api from '../../../utils/api';
import DashboardCard from '../DashboardCard';
import PotentialSpamChip, { isPotentialSpam } from '../PotentialSpamChip';

function RecentFormSubmissionsCard() {
  const navigate = useNavigate();
  const styles = useThemeStyles();
  const [submissions, setSubmissions] = useState({ items: [], loading: true, error: null });

  const fetchSubmissions = useCallback(async () => {
    try {
      setSubmissions(prev => ({ ...prev, loading: true, error: null }));
      const { data } = await api.get('/api/forms/submissions/recent', {
        params: { limit: 5 },
      });

      const items = (data.submissions || []).map((s) => {
        const d = s.data || {};
        const formName = (s.form && typeof s.form === 'object') ? s.form.name : null;
        return {
          id: s._id,
          formId: s.form?._id || s.form,
          formName: formName || 'Form',
          name: d.name || d.Name || 'Unknown',
          email: d.email || d.Email || '',
          createdAt: s.createdAt,
          read: s.read,
          convertedLead: s.convertedLead,
          spamStatus: s.spamStatus,
        };
      });

      setSubmissions({ items, loading: false, error: null });
    } catch (error) {
      setSubmissions({ items: [], loading: false, error: 'Failed to load submissions' });
    }
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  return (
    <DashboardCard
      title="Recent form responses"
      icon={<ClipboardText size={24} weight="regular" />}
      loading={submissions.loading}
      error={submissions.error}
      isEmpty={!submissions.loading && submissions.items.length === 0}
      emptyMessage="No responses yet. Replies from your site forms and questionnaires show up here."
      emptyIcon={ClipboardText}
      menuItems={[
        {
          label: 'View form submissions',
          onClick: () => navigate('/supplier/forms/submissions'),
        },
      ]}
      sx={{ height: '100%', minHeight: '400px' }}
    >
      {submissions.items.length > 0 && <List dense>
        {submissions.items.map((item) => (
          <ListItem
            key={item.id}
            sx={{
              px: 2,
              py: 1.5,
              mb: 1,
              backgroundColor: '#ffffff',
              borderRadius: 1,
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigate('/supplier/forms/submissions');
            }}
          >
            <ListItemText
              primary={
                <Box sx={styles.layout.flexBetween}>
                  <Typography variant="body2" fontWeight={500}>
                    {item.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {item.formName && (
                      <Chip label={item.formName} size="small" variant="outlined" color="default" />
                    )}
                    {isPotentialSpam(item) && <PotentialSpamChip />}
                    {!item.read && (
                      <Chip label="Unread" size="small" color="primary" variant="filled" />
                    )}
                  </Box>
                </Box>
              }
              secondary={
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {item.email}
                  </Typography>
                  {item.createdAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      • {format(new Date(item.createdAt), 'MMM d')}
                    </Typography>
                  )}
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>}
    </DashboardCard>
  );
}

export default RecentFormSubmissionsCard;
